import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { PresignedUploadModel, UploadedFileModel, FilesMapper, validateEncryptedId } from '@shared';
import { InvestigationFilesApi } from '../api/investigation-files.api';

/**
 * Repository for investigation attachment upload operations.
 * Orchestrates API calls and DTO/entity mapping.
 */
@Injectable({ providedIn: 'root' })
export class InvestigationFilesRepository {
  private readonly _api = inject(InvestigationFilesApi);
  private readonly _mapper = inject(FilesMapper);

  /**
   * Gets a presigned S3 upload URL for an investigation attachment.
   *
   * @param investigationId - Encrypted investigation ID
   * @param file - File to upload
   * @returns Observable of presigned upload entity
   * @throws Error if investigation ID is invalid
   */
  public getPresignedUploadUrl(investigationId: string, file: File): Observable<PresignedUploadModel> {
    validateEncryptedId(investigationId, 'investigation ID');

    const requestDto = this._mapper.toPresignedUploadRequestDto(file);
    return this._api.getPresignedUploadUrl(investigationId, requestDto).pipe(map((dto) => this._mapper.toPresignedUploadDomain(dto)));
  }

  /**
   * Confirms a successful upload with the backend and creates investigation attachment.
   *
   * @param investigationId - Encrypted investigation ID
   * @param uploadId - Upload ID from presigned URL response
   * @param file - Original file for metadata
   * @param s3Key - S3 key from presigned upload
   * @returns Observable of uploaded file entity
   * @throws Error if investigation ID is invalid
   */
  public confirmUpload(investigationId: string, uploadId: string, file: File, s3Key: string): Observable<UploadedFileModel> {
    validateEncryptedId(investigationId, 'investigation ID');

    const requestDto = this._mapper.toConfirmUploadRequestDto(uploadId);
    return this._api.confirmUpload(investigationId, requestDto).pipe(map((dto) => this._mapper.toUploadedFileDomain(dto, file, s3Key)));
  }
}
